import { useEffect, useState } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { FileText, LayoutDashboard, Printer, ReceiptText, Settings2 } from 'lucide-react';
import type { KsefEnvironment } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useFiscalApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { ENV_BADGE_CLASS } from './fiscalEnv';

const TABS = [
  { to: '', end: true, icon: LayoutDashboard, labelKey: 'fiscal.tabs.dashboard' },
  { to: 'invoices', end: false, icon: FileText, labelKey: 'fiscal.tabs.invoices' },
  { to: 'receipts', end: false, icon: ReceiptText, labelKey: 'fiscal.tabs.receipts' },
  { to: 'printers', end: false, icon: Printer, labelKey: 'fiscal.tabs.printers' },
  { to: 'settings', end: false, icon: Settings2, labelKey: 'fiscal.tabs.settings' },
] as const;

/**
 * Tabbed shell for the fiscal (KSeF invoices + fiscal receipts) section —
 * mirrors HrTabs' route-driven sub-navigation pattern but uses NavLink
 * directly since every tab is a plain route (no role-based visibility here).
 * The environment badge is fetched once on mount / company switch; a fetch
 * failure just leaves it unset rather than blocking the tabs or outlet.
 */
export function FiscalLayout() {
  const context = useOutletContext<AppShellContext>();
  const { nip } = context;
  const { getSettings } = useFiscalApi();
  const [environment, setEnvironment] = useState<KsefEnvironment | null>(null);

  useEffect(() => {
    let mounted = true;
    getSettings(nip)
      .then((settings) => {
        if (mounted) setEnvironment(settings.environment);
      })
      .catch(() => {
        // Badge just stays unset — the settings/invoices/receipts pages
        // surface their own errors when they load.
      });
    return () => {
      mounted = false;
    };
  }, [nip, getSettings]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{t('fiscal.title')}</h1>
        {environment && (
          // Company DEFAULT environment — individual API tokens may override it,
          // so the suffix keeps the badge honest for mixed-environment invoices.
          <Badge className={cn('font-medium uppercase', ENV_BADGE_CLASS[environment])}>
            {t(`fiscal.environment.${environment}`)} {t('fiscal.environmentDefaultSuffix')}
          </Badge>
        )}
      </div>

      <nav aria-label={t('fiscal.title')} className="bg-muted inline-flex items-center gap-1 self-start rounded-lg p-1">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to || '.'}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            <tab.icon className="size-4" />
            {t(tab.labelKey)}
          </NavLink>
        ))}
      </nav>

      <Outlet context={context} />
    </div>
  );
}
